import { useState, useRef, useCallback, useEffect, useMemo } from 'react';

import useIsMobile from '../hooks/useIsMobile';
import { useSelector } from 'react-redux';
import { selectSymbols } from '../redux/features/platformSlice';
import SymbolList from '../components/watchlist/SymbolList';
import TradingChart from '../components/chart/TradingChart';
import Portfolio from '../components/portfolio/Portfolio';
import TradePanel from '../components/trade/TradePanel';
import MarketOverview from '../screens/MarketOverview';
import Settings from '../screens/Settings';
import Profile from '../screens/Profile';
import { useTheme } from '../theme/ThemeContext';
import Topbar from '../components/layout/Topbar';
import AccountBar from '../components/common/AccountBar';
import InitialRestRequest from '../components/common/InitialRestRequest';
import InitialSocketConnection from '../components/common/InitialSocketConnection';
import { GlobalLoading } from '../components/common/Loading';
import { selectIsInitialized } from '../redux/features/platformSlice';
import { ToastContainer } from '../components/common/ToastNotification';
import { getMarketWatchSymbols } from '../utils/symbolPriceFilter';

// ── Desktop layout ────────────────────────────────────────────────────────────
const DESKTOP_SYMBOL_WIDTH_KEY = 'desktop_symbol_list_width';
const DESKTOP_TRADE_PANEL_WIDTH_KEY = 'desktop_trade_panel_width';
const DESKTOP_PORTFOLIO_HEIGHT_KEY = 'desktop_portfolio_height';
const MIN_SYMBOL_LIST_WIDTH = 270;
const MAX_SYMBOL_LIST_WIDTH = 520;
const MIN_TRADING_WORKSPACE_WIDTH = 480;
const DEFAULT_TRADE_PANEL_WIDTH = 251;
const MIN_TRADE_PANEL_WIDTH = 248;
const MAX_TRADE_PANEL_WIDTH = 520;
const DEFAULT_PORTFOLIO_HEIGHT = 250;
const MIN_CHART_PANEL_HEIGHT = 260;
const MIN_PORTFOLIO_HEIGHT = 140;
const MAX_PORTFOLIO_HEIGHT = 420;
const RESIZE_HANDLE_WIDTH = 8;
const RESIZE_HANDLE_HEIGHT = 8;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getStoredSymbolListWidth = () => {
    const width = Number(localStorage.getItem(DESKTOP_SYMBOL_WIDTH_KEY));
    return Number.isFinite(width)
        ? clamp(width, MIN_SYMBOL_LIST_WIDTH, MAX_SYMBOL_LIST_WIDTH)
        : 320;
};

const getStoredTradePanelWidth = () => {
    const width = Number(localStorage.getItem(DESKTOP_TRADE_PANEL_WIDTH_KEY));
    return Number.isFinite(width)
        ? clamp(width, MIN_TRADE_PANEL_WIDTH, MAX_TRADE_PANEL_WIDTH)
        : DEFAULT_TRADE_PANEL_WIDTH;
};

const getStoredPortfolioHeight = () => {
    const height = Number(localStorage.getItem(DESKTOP_PORTFOLIO_HEIGHT_KEY));
    return Number.isFinite(height)
        ? clamp(height, MIN_PORTFOLIO_HEIGHT, MAX_PORTFOLIO_HEIGHT)
        : DEFAULT_PORTFOLIO_HEIGHT;
};

const DesktopApp = ({ activeNav, setActiveNav, activeSymbol, handleSelectSymbol, theme }) => {
    const tradeLayoutRef = useRef(null);
    const chartWorkspaceRef = useRef(null);
    const [symbolListWidth, setSymbolListWidth] = useState(getStoredSymbolListWidth);
    const [tradePanelWidth, setTradePanelWidth] = useState(getStoredTradePanelWidth);
    const [portfolioHeight, setPortfolioHeight] = useState(getStoredPortfolioHeight);
    const [isResizingSymbolList, setIsResizingSymbolList] = useState(false);
    const [isResizingTradePanel, setIsResizingTradePanel] = useState(false);
    const [isResizingChartPanel, setIsResizingChartPanel] = useState(false);

    const startSymbolListResize = useCallback((event) => {
        event.preventDefault();

        const layout = tradeLayoutRef.current;
        if (!layout) return;

        const startX = event.clientX;
        const startWidth = symbolListWidth;
        const maxAvailableWidth = Math.max(
            MIN_SYMBOL_LIST_WIDTH,
            layout.clientWidth - tradePanelWidth - MIN_TRADING_WORKSPACE_WIDTH - (RESIZE_HANDLE_WIDTH * 2)
        );
        const maxWidth = Math.min(MAX_SYMBOL_LIST_WIDTH, maxAvailableWidth);

        setIsResizingSymbolList(true);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        const handlePointerMove = (moveEvent) => {
            const nextWidth = clamp(startWidth + moveEvent.clientX - startX, MIN_SYMBOL_LIST_WIDTH, maxWidth);
            setSymbolListWidth(nextWidth);
        };

        const handlePointerUp = (upEvent) => {
            const nextWidth = clamp(startWidth + upEvent.clientX - startX, MIN_SYMBOL_LIST_WIDTH, maxWidth);
            localStorage.setItem(DESKTOP_SYMBOL_WIDTH_KEY, String(Math.round(nextWidth)));
            setSymbolListWidth(nextWidth);
            setIsResizingSymbolList(false);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
            window.removeEventListener('pointercancel', handlePointerUp);
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        window.addEventListener('pointercancel', handlePointerUp);
    }, [symbolListWidth, tradePanelWidth]);

    const startTradePanelResize = useCallback((event) => {
        event.preventDefault();

        const layout = tradeLayoutRef.current;
        if (!layout) return;

        const startX = event.clientX;
        const startWidth = tradePanelWidth;
        const maxAvailableWidth = Math.max(
            MIN_TRADE_PANEL_WIDTH,
            layout.clientWidth - symbolListWidth - MIN_TRADING_WORKSPACE_WIDTH - (RESIZE_HANDLE_WIDTH * 2)
        );
        const maxWidth = Math.min(MAX_TRADE_PANEL_WIDTH, maxAvailableWidth);

        setIsResizingTradePanel(true);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        const handlePointerMove = (moveEvent) => {
            const nextWidth = clamp(startWidth + startX - moveEvent.clientX, MIN_TRADE_PANEL_WIDTH, maxWidth);
            setTradePanelWidth(nextWidth);
        };

        const handlePointerUp = (upEvent) => {
            const nextWidth = clamp(startWidth + startX - upEvent.clientX, MIN_TRADE_PANEL_WIDTH, maxWidth);
            localStorage.setItem(DESKTOP_TRADE_PANEL_WIDTH_KEY, String(Math.round(nextWidth)));
            setTradePanelWidth(nextWidth);
            setIsResizingTradePanel(false);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
            window.removeEventListener('pointercancel', handlePointerUp);
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        window.addEventListener('pointercancel', handlePointerUp);
    }, [symbolListWidth, tradePanelWidth]);

    const startChartPanelResize = useCallback((event) => {
        event.preventDefault();

        const workspace = chartWorkspaceRef.current;
        if (!workspace) return;

        const startY = event.clientY;
        const startHeight = portfolioHeight;
        const maxAvailableHeight = Math.max(
            MIN_PORTFOLIO_HEIGHT,
            workspace.clientHeight - MIN_CHART_PANEL_HEIGHT - RESIZE_HANDLE_HEIGHT
        );
        const maxHeight = Math.min(MAX_PORTFOLIO_HEIGHT, maxAvailableHeight);

        setIsResizingChartPanel(true);
        document.body.style.cursor = 'row-resize';
        document.body.style.userSelect = 'none';

        const handlePointerMove = (moveEvent) => {
            const nextHeight = clamp(startHeight + startY - moveEvent.clientY, MIN_PORTFOLIO_HEIGHT, maxHeight);
            setPortfolioHeight(nextHeight);
        };

        const handlePointerUp = (upEvent) => {
            const nextHeight = clamp(startHeight + startY - upEvent.clientY, MIN_PORTFOLIO_HEIGHT, maxHeight);
            localStorage.setItem(DESKTOP_PORTFOLIO_HEIGHT_KEY, String(Math.round(nextHeight)));
            setPortfolioHeight(nextHeight);
            setIsResizingChartPanel(false);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
            window.removeEventListener('pointercancel', handlePointerUp);
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        window.addEventListener('pointercancel', handlePointerUp);
    }, [portfolioHeight]);

    return (
        <>
            {activeNav === 'trade' && (
                <div ref={tradeLayoutRef} style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                    {/* symbol list */}
                    <div style={{ width: symbolListWidth, minWidth: MIN_SYMBOL_LIST_WIDTH, maxWidth: MAX_SYMBOL_LIST_WIDTH, flexShrink: 0, overflow: 'hidden' }}>
                        <SymbolList activeSymbol={activeSymbol?.symbol} onSelectSymbol={handleSelectSymbol} />
                    </div>
                    <div
                        aria-label="Resize symbol list"
                        role="separator"
                        aria-orientation="vertical"
                        onPointerDown={startSymbolListResize}
                        style={{
                            width: RESIZE_HANDLE_WIDTH,
                            flexShrink: 0,
                            cursor: 'col-resize',
                            background: isResizingSymbolList ? theme.primaryGlow : theme.surface,
                            borderLeft: `1px solid ${theme.border}`,
                            borderRight: `1px solid ${theme.border}`,
                            position: 'relative',
                            touchAction: 'none',
                        }}
                    >
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            width: 2,
                            height: 32,
                            borderRadius: 2,
                            background: isResizingSymbolList ? theme.primary : theme.textS,
                            opacity: isResizingSymbolList ? 1 : 0.35,
                            transform: 'translate(-50%, -50%)',
                        }} />
                    </div>
                    {/* trading chart and portfolio */}
                    <div ref={chartWorkspaceRef} style={{ flex: 1, minWidth: MIN_TRADING_WORKSPACE_WIDTH, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <div style={{ flex: 1, overflow: 'hidden' }}>
                            <TradingChart symbol={activeSymbol?.symbol} item={activeSymbol} />
                        </div>
                        <div
                            aria-label="Resize trading chart panel"
                            role="separator"
                            aria-orientation="horizontal"
                            onPointerDown={startChartPanelResize}
                            style={{
                                height: RESIZE_HANDLE_HEIGHT,
                                flexShrink: 0,
                                cursor: 'row-resize',
                                background: isResizingChartPanel ? theme.primaryGlow : theme.surface,
                                borderTop: `1px solid ${theme.border}`,
                                borderBottom: `1px solid ${theme.border}`,
                                position: 'relative',
                                touchAction: 'none',
                            }}
                        >
                            <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                width: 34,
                                height: 2,
                                borderRadius: 2,
                                background: isResizingChartPanel ? theme.primary : theme.textS,
                                opacity: isResizingChartPanel ? 1 : 0.35,
                                transform: 'translate(-50%, -50%)',
                            }} />
                        </div>
                        <div style={{ height: portfolioHeight, flexShrink: 0, overflow: 'hidden' }}>
                            <Portfolio />
                        </div>
                    </div>
                    <div
                        aria-label="Resize trade panel"
                        role="separator"
                        aria-orientation="vertical"
                        onPointerDown={startTradePanelResize}
                        style={{
                            width: RESIZE_HANDLE_WIDTH,
                            flexShrink: 0,
                            cursor: 'col-resize',
                            background: isResizingTradePanel ? theme.primaryGlow : theme.surface,
                            borderLeft: `1px solid ${theme.border}`,
                            borderRight: `1px solid ${theme.border}`,
                            position: 'relative',
                            touchAction: 'none',
                        }}
                    >
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            width: 2,
                            height: 32,
                            borderRadius: 2,
                            background: isResizingTradePanel ? theme.primary : theme.textS,
                            opacity: isResizingTradePanel ? 1 : 0.35,
                            transform: 'translate(-50%, -50%)',
                        }} />
                    </div>
                    <div style={{ width: tradePanelWidth, flexShrink: 0, overflow: 'hidden' }}>
                        <TradePanel symbol={activeSymbol?.symbol} item={activeSymbol} />
                    </div>
                </div>
            )}
            {activeNav === 'markets' && (
                <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        <MarketOverview onSelectSymbol={handleSelectSymbol} />
                    </div>
                </div>
            )}
            {activeNav === 'portfolio' && (
                <div style={{ flex: 1, overflow: 'hidden' }}>
                    <Portfolio />
                </div>
            )}
            {activeNav === 'watchlist' && (
                <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
                    <div style={{ width: 260, flexShrink: 0, borderRight: `1px solid ${theme.border}` }}>
                        <SymbolList activeSymbol={activeSymbol?.symbol} onSelectSymbol={handleSelectSymbol} />
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: theme.text, marginBottom: 12 }}>Watchlist</div>
                        <MarketOverview onSelectSymbol={handleSelectSymbol} />
                    </div>
                </div>
            )}
            {activeNav === 'settings' && (
                <div style={{ flex: 1, overflowY: 'auto' }}>
                    <Settings />
                </div>
            )}
            {activeNav === 'profile' && (
                <div style={{ flex: 1, overflowY: 'auto' }}>
                    <Profile />
                </div>
            )}
        </>
    );
};

// ── Mobile layout ─────────────────────────────────────────────────────────────
const MobileApp = ({ activeNav, setActiveNav, activeSymbol, handleSelectSymbol, theme }) => {
    const [mobilePane, setMobilePane] = useState('chart');

    return (
        <>
            {activeNav === 'trade' && (
                <>
                    <div style={{ display: 'flex', background: theme.surface, borderBottom: `1px solid ${theme.border}`, flexShrink: 0 }}>
                        {[{ id: 'symbols', label: 'Symbols' }, { id: 'chart', label: 'Chart' }, { id: 'panel', label: 'Trade' }].map(({ id, label }) => (
                            <button key={id} onClick={() => setMobilePane(id)} style={{
                                flex: 1, padding: '10px 4px', border: 'none', cursor: 'pointer',
                                fontFamily: theme.sans, fontSize: 11, fontWeight: 600,
                                background: mobilePane === id ? theme.primaryGlow : 'transparent',
                                color: mobilePane === id ? theme.primaryL : theme.textS,
                                borderBottom: `2px solid ${mobilePane === id ? theme.primary : 'transparent'}`,
                            }}>{label}</button>
                        ))}
                    </div>

                    <div style={{ flex: 1, overflow: 'hidden' }}>
                        {mobilePane === 'symbols' && (
                            <SymbolList
                                activeSymbol={activeSymbol?.symbol}
                                onSelectSymbol={(sym) => { handleSelectSymbol(sym); setMobilePane('chart'); }}
                            />
                        )}
                        {mobilePane === 'chart' && (
                            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <div style={{ flex: 1, overflow: 'hidden' }}>
                                    <TradingChart symbol={activeSymbol?.symbol} item={activeSymbol} />
                                </div>
                                <div style={{ height: 160, borderTop: `1px solid ${theme.border}`, overflow: 'hidden' }}>
                                    <Portfolio />
                                </div>
                            </div>
                        )}
                        {mobilePane === 'panel' && (
                            <TradePanel symbol={activeSymbol?.symbol} item={activeSymbol} />
                        )}
                    </div>
                </>
            )}
            {activeNav === 'markets' && <div style={{ flex: 1, overflowY: 'auto' }}><MarketOverview onSelectSymbol={handleSelectSymbol} /></div>}
            {activeNav === 'portfolio' && <div style={{ flex: 1, overflow: 'hidden' }}><Portfolio /></div>}
            {activeNav === 'watchlist' && <div style={{ flex: 1, overflow: 'hidden' }}><SymbolList activeSymbol={activeSymbol?.symbol} onSelectSymbol={handleSelectSymbol} /></div>}
            {activeNav === 'settings' && <div style={{ flex: 1, overflowY: 'auto' }}><Settings /></div>}
            {activeNav === 'profile' && <div style={{ flex: 1, overflowY: 'auto' }}><Profile /></div>}
        </>
    );
};

// ── Dashboard page ────────────────────────────────────────────────────────────
const Dashboard = () => {
    const theme = useTheme();
    const isMobile = useIsMobile(768);
    const symbols = useSelector(selectSymbols);
    const isInitialized = useSelector(selectIsInitialized);
    const marketWatchSymbols = useMemo(() => getMarketWatchSymbols(symbols), [symbols]);

    const [activeNav, setActiveNav] = useState('trade');
    const [activeSymbol, setActiveSymbol] = useState(null);
    // const visibleSymbols = useMemo(
    //     () => filterSymbolsByRealtimePrice(symbols),
    //     [symbols]
    // );

    useEffect(() => {
        if (!activeSymbol && marketWatchSymbols.length) {
            setActiveSymbol(marketWatchSymbols[0]);
        }
    }, [activeSymbol, marketWatchSymbols]);

    const handleSelectSymbol = (item) => {
        setActiveSymbol(item);
        if (activeNav !== 'trade') setActiveNav('trade');
    };

    if (!isInitialized) {
        return (
            <>
                <InitialRestRequest />
                <GlobalLoading />
            </>
        );
    }

    return (
        <div style={{
            display: 'flex', flexDirection: 'column', height: '100dvh',
            background: theme.bg, fontFamily: theme.sans, color: theme.text,
            overflow: 'hidden', transition: 'background 0.3s, color 0.3s',
        }}>
            {/* Background service components — render null, run side-effects */}
            <InitialRestRequest />
            <InitialSocketConnection />

            <Topbar
                activeNav={activeNav}
                setActiveNav={setActiveNav}
                activeSymbol={activeSymbol?.symbol}
                activeItem={activeSymbol}
            />
            <AccountBar isMobile={isMobile} />

            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                {isMobile
                    ? <MobileApp activeNav={activeNav} setActiveNav={setActiveNav} activeSymbol={activeSymbol} handleSelectSymbol={handleSelectSymbol} theme={theme} />
                    : <DesktopApp activeNav={activeNav} setActiveNav={setActiveNav} activeSymbol={activeSymbol} handleSelectSymbol={handleSelectSymbol} theme={theme} />
                }
            </div>
            <ToastContainer />
        </div>
    );
};

export default Dashboard;
